import React from "react";
import { Stack, TextInput, Select, Grid } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface CountdownBlockSettingsData {
  targetDateTime: string; // UTC ISO string
  title?: string;
  completedText?: string;
  confettiType?: string;
  loopConfetti?: boolean;
}

export interface CountdownBlockSettingsProps {
  /** Current settings data */
  value: CountdownBlockSettingsData | null;

  /** Callback when settings change */
  onChange: (settings: CountdownBlockSettingsData) => void;

  /** Validation errors for specific fields */
  errors?: Partial<Record<keyof CountdownBlockSettingsData, string>>;
}

const CONFETTI_OPTIONS = [
  { value: "celebration", label: "Celebration" },
  { value: "fireworks", label: "Fireworks" },
  { value: "snow", label: "Snow" },
  { value: "burst", label: "Burst" },
  { value: "stars", label: "Stars" },
  { value: "none", label: "None" },
];

const LOOP_CONFETTI_OPTIONS = [
  { value: "false", label: "No" },
  { value: "true", label: "Yes" },
];

export const CountdownBlockSettings: React.FC<CountdownBlockSettingsProps> = ({
  value,
  onChange,
  errors = {},
}) => {
  // Initialize default values if not provided
  const settings: CountdownBlockSettingsData = value || {
    targetDateTime: dayjs().add(1, "day").toISOString(),
    title: "",
    completedText: "Time's up!",
    confettiType: "celebration",
    loopConfetti: false,
  };

  const handleChange = (
    field: keyof CountdownBlockSettingsData,
    newValue: string | boolean | Date | null
  ) => {
    const updatedSettings = { ...settings };

    if (
      field === "targetDateTime" &&
      (newValue instanceof Date || typeof newValue === "string")
    ) {
      // Convert local date/time to UTC ISO string
      updatedSettings.targetDateTime = dayjs(newValue)
        .utc()
        .format("YYYY-MM-DDTHH:mm:00.000Z");
    } else if (field === "loopConfetti") {
      updatedSettings.loopConfetti = newValue === true || newValue === "true";
    } else {
      updatedSettings[field] = newValue as any;
    }

    onChange(updatedSettings);
  };

  // Convert UTC string back to local Date for the picker
  const getLocalDateValue = (): Date | null => {
    if (!settings.targetDateTime) return null;
    return dayjs(settings.targetDateTime).local().toDate();
  };

  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={12}>
          <DateTimePicker
            label="Target Date & Time"
            placeholder="Select countdown target date and time"
            value={getLocalDateValue()}
            onChange={(date) => handleChange("targetDateTime", date)}
            error={errors.targetDateTime}
            required
            withAsterisk
            clearable={false}
            minDate={new Date()}
            valueFormat="YYYY-MM-DD hh:mm A"
            timePickerProps={{
              withDropdown: true,
              //popoverProps: { withinPortal: false },
              format: "12h",
            }}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <TextInput
            label="Title"
            placeholder="Enter countdown title (optional)"
            value={settings.title || ""}
            onChange={(e) => handleChange("title", e.target.value)}
            error={errors.title}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <TextInput
            label="Completed Text"
            placeholder="Text to show when countdown finishes"
            value={settings.completedText || ""}
            onChange={(e) => handleChange("completedText", e.target.value)}
            error={errors.completedText}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Confetti Effect"
            placeholder="Select confetti type"
            value={settings.confettiType || "celebration"}
            onChange={(value) => handleChange("confettiType", value)}
            data={CONFETTI_OPTIONS}
            error={errors.confettiType}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Loop Confetti"
            placeholder="Loop confetti effect?"
            value={settings.loopConfetti ? "true" : "false"}
            onChange={(value) => handleChange("loopConfetti", value === "true")}
            data={LOOP_CONFETTI_OPTIONS}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
